import React, { useState } from 'react';
import { doc, updateDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db } from '../config/firebase';

/**
 * I9DatePicker — lets an employer schedule the I-9 verification appointment.
 *
 * Props:
 *   i9RequestId  {string}  — Firestore document ID in i9_requests
 *   onScheduled  {fn}      — optional callback after a successful write
 */
function I9DatePicker({ i9RequestId, onScheduled }) {
  const [datetimeValue, setDatetimeValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const minDatetime = () => {
    // Build an ISO-like string truncated to the minute for the min attribute
    const now = new Date(Date.now() + 60 * 1000); // at least 1 minute ahead
    const pad = (n) => String(n).padStart(2, '0');
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
  };

  const handleConfirm = async () => {
    if (!datetimeValue) {
      setError('Please select a date and time.');
      return;
    }

    const selectedDate = new Date(datetimeValue);
    if (selectedDate <= new Date()) {
      setError('Appointment must be in the future.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await updateDoc(doc(db, 'i9_requests', i9RequestId), {
        appointment_time: Timestamp.fromDate(selectedDate),
        appointment_status: 'scheduled',
        last_updated: serverTimestamp(),
      });

      setSuccess(true);
      if (onScheduled) onScheduled(selectedDate);
    } catch (err) {
      console.error('Error scheduling I-9 appointment:', err);
      setError('Failed to save appointment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div style={styles.successBox}>
        <span style={styles.successIcon}>&#10003;</span>
        <span> Appointment scheduled successfully.</span>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <h3 style={styles.heading}>Schedule I-9 Appointment</h3>

      <label style={styles.label} htmlFor="i9-datetime">
        Select date &amp; time
      </label>
      <input
        id="i9-datetime"
        type="datetime-local"
        min={minDatetime()}
        value={datetimeValue}
        onChange={(e) => setDatetimeValue(e.target.value)}
        style={styles.input}
        disabled={loading}
      />

      {error && <p style={styles.errorText}>{error}</p>}

      <button
        onClick={handleConfirm}
        disabled={loading || !datetimeValue}
        style={loading || !datetimeValue ? { ...styles.button, ...styles.buttonDisabled } : styles.button}
      >
        {loading ? 'Saving…' : 'Confirm Appointment'}
      </button>
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    padding: '20px',
    background: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    maxWidth: '360px',
  },
  heading: {
    margin: 0,
    fontSize: '16px',
    fontWeight: '600',
    color: '#1a202c',
  },
  label: {
    fontSize: '14px',
    color: '#4a5568',
    fontWeight: '500',
  },
  input: {
    padding: '8px 12px',
    fontSize: '14px',
    border: '1px solid #cbd5e0',
    borderRadius: '6px',
    outline: 'none',
    color: '#1a202c',
  },
  errorText: {
    margin: 0,
    fontSize: '13px',
    color: '#e53e3e',
  },
  button: {
    padding: '10px 16px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#ffffff',
    background: '#3182ce',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  buttonDisabled: {
    background: '#a0aec0',
    cursor: 'not-allowed',
  },
  successBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '14px 18px',
    background: '#f0fff4',
    border: '1px solid #9ae6b4',
    borderRadius: '8px',
    fontSize: '14px',
    color: '#276749',
    fontWeight: '500',
  },
  successIcon: {
    fontSize: '18px',
    color: '#38a169',
  },
};

export default I9DatePicker;
